import { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Play } from 'lucide-react';

const videoFeedback = [
    {
        id: 1,
        youtubeId: "d4-FCEY8WmQ",
        author: "Simon Whitehead",
        company: "Take Your Medicine",
        title: "From 4 hours to 15 minutes",
    },
    {
        id: 2,
        youtubeId: "jxm-_9uGbL4",
        author: "Amr Moselhy",
        company: "Surgical Clear",
        title: "Quick on time quality delivery",
    },
    {
        id: 3,
        youtubeId: "uLn-ZLONaro",
        author: "Gordana Banovic",
        company: "Jarvis Consulting Group",
        title: "Report and Google Sheet Automation",
    },
    {
        id: 4,
        youtubeId: "9__AUJdNtnQ",
        author: "Dave Goodman",
        company: "Multiple Businesses",
        title: "Complex calculation and requirement simplified",
    },
];

export const VideoFeedbackCarousel = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false); // Track if current slide is playing

    const goToPrevious = () => {
        setIsPlaying(false); // Stop video when changing slides
        setCurrentIndex((prev) => (prev - 1 + videoFeedback.length) % videoFeedback.length);
    };

    const goToNext = () => {
        setIsPlaying(false); // Stop video when changing slides
        setCurrentIndex((prev) => (prev + 1) % videoFeedback.length);
    };

    const currentVideo = videoFeedback[currentIndex];

    return (
        <section id="video-feedback" className="py-20 md:py-28 bg-white" data-testid="video-feedback-section">
            <div className="max-w-6xl mx-auto px-6 md:px-12">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="text-center mb-12"
                >
                    <p className="text-[#1E8E3E] text-sm font-bold uppercase tracking-widest mb-4">
                        Video Stories
                    </p>
                    <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#334155]">
                        Hear From Clients
                    </h2>
                </motion.div>

                {/* Video Carousel Container */}
                <div className="relative max-w-4xl mx-auto">
                    <motion.div
                        key={currentIndex}
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3 }}
                    >
                        {/* Video Card Area */}
                        <div className="relative aspect-video rounded-2xl overflow-hidden bg-slate-100 border border-slate-200 shadow-xl group">
                            
                            {/* LOGIC: If playing, show Iframe. If not, show Thumbnail. */}
                            {isPlaying ? (
                                <iframe 
                                    src={`https://www.youtube.com/embed/${currentVideo.youtubeId}?autoplay=1&rel=0`} 
                                    className="w-full h-full" 
                                    title="Client Video"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                    allowFullScreen
                                />
                            ) : (
                                // THUMBNAIL MODE
                                <div 
                                    className="w-full h-full cursor-pointer" 
                                    onClick={() => setIsPlaying(true)}
                                >
                                    <img
                                        src={`https://img.youtube.com/vi/${currentVideo.youtubeId}/maxresdefault.jpg`}
                                        onError={(e) => { e.target.src = `https://img.youtube.com/vi/${currentVideo.youtubeId}/hqdefault.jpg` }}
                                        alt={currentVideo.title}
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                                        <div className="w-20 h-20 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                                            <Play size={32} fill="#1E8E3E" className="text-[#1E8E3E] ml-1" />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Caption */}
                        <div className="mt-8 text-center">
                            <h3 className="text-[#334155] font-bold text-xl mb-1">{currentVideo.title}</h3>
                            <p className="text-[#64748B] text-sm font-medium uppercase tracking-wide">
                                {currentVideo.author}, {currentVideo.company}
                            </p>
                        </div>
                    </motion.div>

                    {/* Navigation Buttons */}
                    <div className="flex items-center justify-center gap-6 mt-10">
                        <button
                            onClick={goToPrevious}
                            className="w-12 h-12 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-600 hover:text-[#1E8E3E] hover:border-[#1E8E3E] transition-all duration-200 hover:-translate-x-1"
                        >
                            <ChevronLeft size={24} />
                        </button>
                        
                        <div className="flex items-center gap-3">
                            {videoFeedback.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => {
                                        setIsPlaying(false);
                                        setCurrentIndex(index);
                                    }}
                                    className={`h-2 rounded-full transition-all duration-300 ${
                                        index === currentIndex ? 'w-8 bg-[#1E8E3E]' : 'w-2 bg-slate-300 hover:bg-slate-400'
                                    }`}
                                />
                            ))}
                        </div>

                        <button
                            onClick={goToNext}
                            className="w-12 h-12 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-600 hover:text-[#1E8E3E] hover:border-[#1E8E3E] transition-all duration-200 hover:translate-x-1"
                        >
                            <ChevronRight size={24} />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
};